import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Ramp } from "../../../../../types";
import { PREFIXED_COLORS, TAG_OPTIONS } from "../RampCanvas/ShapeEditDialog";
import { isShapeWithinRampBounds } from "./gisUtils";

interface ImportedGISShape {
  id: string;
  label: string;
  originalPolygon: number[][];
  pixelPoints: { x: number; y: number }[];
  tags: string[];
  stroke_style: string;
  fill_style: string;
  font_size: number;
  text: string;
  // Additional optional shape attributes
  type?: string;
  locked?: boolean;
  line_style?: string;
  line_width?: number;
  fill_pattern?: string;
}

type Props = {
  shapes: ImportedGISShape[];
  setShapes: React.Dispatch<React.SetStateAction<ImportedGISShape[]>>;
  ramp: Ramp;
};

export const GISShapeEditGrid: React.FC<Props> = ({
  shapes,
  setShapes,
  ramp,
}) => {
  const { t } = useTranslation();
  const [editingShapeId, setEditingShapeId] = React.useState<string | null>(
    null
  );
  const [inlineEditingFields, setInlineEditingFields] = React.useState<
    Record<string, Set<string>>
  >({});
  const [localValues, setLocalValues] = React.useState<
    Record<string, Record<string, any>>
  >({});

  const toggleInlineEdit = React.useCallback(
    (shapeId: string, field: string) => {
      setInlineEditingFields((prev) => {
        const shapeFields = prev[shapeId] || new Set();
        const newShapeFields = new Set(shapeFields);

        if (newShapeFields.has(field)) {
          newShapeFields.delete(field);
        } else {
          newShapeFields.add(field);
          // Initialize local value when starting to edit
          const shape = shapes.find((s) => s.id === shapeId);
          if (shape) {
            setLocalValues((prevLocal) => ({
              ...prevLocal,
              [shapeId]: {
                ...prevLocal[shapeId],
                [field]: shape[field as keyof ImportedGISShape],
              },
            }));
          }
        }

        return {
          ...prev,
          [shapeId]: newShapeFields,
        };
      });
    },
    [shapes]
  );

  const isInlineEditing = React.useCallback(
    (shapeId: string, field: string) => {
      return inlineEditingFields[shapeId]?.has(field) || false;
    },
    [inlineEditingFields]
  );

  const getFieldValue = React.useCallback(
    (shape: ImportedGISShape, field: string) => {
      // Return local value if we're editing this field, otherwise return the shape's value
      if (
        isInlineEditing(shape.id, field) &&
        localValues[shape.id]?.[field] !== undefined
      ) {
        return localValues[shape.id][field];
      }
      return shape[field as keyof ImportedGISShape];
    },
    [isInlineEditing, localValues]
  );

  const updateLocalValue = React.useCallback(
    (shapeId: string, field: string, value: any) => {
      setLocalValues((prev) => ({
        ...prev,
        [shapeId]: {
          ...prev[shapeId],
          [field]: value,
        },
      }));
    },
    []
  );

  const commitLocalValue = React.useCallback(
    (shapeId: string, field: string) => {
      const localValue = localValues[shapeId]?.[field];
      if (localValue !== undefined) {
        updateShape(shapeId, { [field]: localValue });
      }
      toggleInlineEdit(shapeId, field);
    },
    [localValues, toggleInlineEdit]
  );

  const updateShape = React.useCallback(
    (shapeId: string, updates: Partial<ImportedGISShape>) => {
      setShapes((shapes) =>
        shapes.map((shape) =>
          shape.id === shapeId ? { ...shape, ...updates } : shape
        )
      );
    },
    [setShapes]
  );

  const removeShape = (shapeId: string) => {
    setShapes((shapes) => shapes.filter((shape) => shape.id !== shapeId));
  };

  const handleTagToggle = (shapeId: string, tag: string) => {
    const shape = shapes.find((s) => s.id === shapeId);
    if (!shape) return;

    const currentTags = shape.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];

    updateShape(shapeId, { tags: newTags });
  };

  const ShapeEditRow: React.FC<{ shape: ImportedGISShape }> = ({ shape }) => {
    const isEditing = editingShapeId === shape.id;
    const isWithinBounds = isShapeWithinRampBounds(shape.originalPolygon, ramp);

    if (!isEditing) {
      return (
        <TableRow key={shape.id}>
          <TableCell>
            <Stack direction="row" spacing={1} alignItems="center">
              {!isWithinBounds && (
                <Tooltip title={t('settings.rampsSettings.rampEditor.gisShapeEditGrid.warningOutOfBounds')}>
                  <WarningIcon color="warning" fontSize="small" />
                </Tooltip>
              )}
              {isInlineEditing(shape.id, "text") ? (
                <TextField
                  size="small"
                  value={shape.text}
                  onChange={(e) =>
                    updateShape(shape.id, { text: e.target.value })
                  }
                  onBlur={() => toggleInlineEdit(shape.id, "text")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Escape") {
                      toggleInlineEdit(shape.id, "text");
                    }
                  }}
                  autoFocus
                />
              ) : (
                <Box
                  onClick={() => toggleInlineEdit(shape.id, "text")}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {shape.text || shape.label}
                </Box>
              )}
            </Stack>
          </TableCell>
          <TableCell>
            <Stack direction="row" spacing={0.5}>
              {shape.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Stack>
          </TableCell>
          <TableCell>
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: shape.fill_style,
                border: `2px solid ${shape.stroke_style}`,
                borderRadius: 1,
              }}
            />
          </TableCell>
          <TableCell>
            {isInlineEditing(shape.id, "font_size") ? (
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 8, max: 64 }}
                value={getFieldValue(shape, "font_size")}
                onChange={(e) =>
                  updateLocalValue(
                    shape.id,
                    "font_size",
                    Number(e.target.value)
                  )
                }
                onBlur={() => commitLocalValue(shape.id, "font_size")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    commitLocalValue(shape.id, "font_size");
                  } else if (e.key === "Escape") {
                    toggleInlineEdit(shape.id, "font_size");
                  }
                }}
                autoFocus
                sx={{ width: 80 }}
              />
            ) : (
              <Box
                onClick={() => toggleInlineEdit(shape.id, "font_size")}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "action.hover" },
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {shape.font_size}
              </Box>
            )}
          </TableCell>
          <TableCell>
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => setEditingShapeId(shape.id)}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => removeShape(shape.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow key={shape.id}>
        <TableCell>
          <TextField
            size="small"
            fullWidth
            value={getFieldValue(shape, "text")}
            onChange={(e) => {
              if (isEditing) {
                updateLocalValue(shape.id, "text", e.target.value);
              } else {
                updateShape(shape.id, { text: e.target.value });
              }
            }}
            onBlur={() => {
              if (isEditing) {
                commitLocalValue(shape.id, "text");
              }
            }}
            placeholder={t('settings.rampsSettings.rampEditor.gisShapeEditGrid.placeholderShapeText')}
          />
        </TableCell>
        <TableCell>
          <Stack direction="column" spacing={1}>
            {TAG_OPTIONS.map((tag) => (
              <FormControlLabel
                key={tag}
                control={
                  <Checkbox
                    checked={shape.tags?.includes(tag) || false}
                    onChange={() => handleTagToggle(shape.id, tag)}
                    size="small"
                  />
                }
                label={tag}
                sx={{ margin: 0 }}
              />
            ))}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="column" spacing={1}>
            <TextField
              size="small"
              label={t('settings.rampsSettings.rampEditor.gisShapeEditGrid.presetLabel')}
              select
              value=""
              onChange={(e) => {
                const preset = PREFIXED_COLORS.find(
                  (p) => p.label === e.target.value
                );
                if (preset) {
                  updateShape(shape.id, {
                    stroke_style: preset.stroke_style,
                    fill_style: preset.fill_style,
                  });
                }
              }}
            >
              {PREFIXED_COLORS.map((preset) => (
                <MenuItem key={preset.label} value={preset.label}>
                  {preset.label}
                </MenuItem>
              ))}
            </TextField>
            <MuiColorInput
              size="small"
              label={t('settings.rampsSettings.rampEditor.gisShapeEditGrid.strokeLabel')}
              value={shape.stroke_style}
              onChange={(value) =>
                updateShape(shape.id, { stroke_style: value })
              }
            />
            <MuiColorInput
              size="small"
              label={t('settings.rampsSettings.rampEditor.gisShapeEditGrid.fillLabel')}
              value={shape.fill_style}
              onChange={(value) => updateShape(shape.id, { fill_style: value })}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 8, max: 64 }}
            value={getFieldValue(shape, "font_size")}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (isEditing) {
                updateLocalValue(shape.id, "font_size", value);
              } else {
                updateShape(shape.id, { font_size: value });
              }
            }}
            onBlur={() => {
              if (isEditing) {
                commitLocalValue(shape.id, "font_size");
              }
            }}
          />
        </TableCell>
        <TableCell>
          <Button size="small" onClick={() => setEditingShapeId(null)}>
            {t('settings.rampsSettings.rampEditor.gisShapeEditGrid.doneButton')}
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  if (shapes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center">
        {t('settings.rampsSettings.rampEditor.gisShapeEditGrid.noShapes')}
      </Typography>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('settings.rampsSettings.rampEditor.gisShapeEditGrid.instructions')}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            // Apply default styling to all shapes
            const defaultUpdates = {
              stroke_style: "#3c6ce9",
              fill_style: "rgba(60, 108, 233, 0.1)",
              font_size: 14,
              tags: ["parkable"],
            };
            setShapes((shapes) =>
              shapes.map((shape) => ({ ...shape, ...defaultUpdates }))
            );
          }}
        >
          {t('settings.rampsSettings.rampEditor.gisShapeEditGrid.applyDefaultStyling')}
        </Button>
      </Stack>

      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('settings.rampsSettings.rampEditor.gisShapeEditGrid.headers.textLabel')}</TableCell>
              <TableCell>{t('settings.rampsSettings.rampEditor.gisShapeEditGrid.headers.tags')}</TableCell>
              <TableCell>{t('settings.rampsSettings.rampEditor.gisShapeEditGrid.headers.colors')}</TableCell>
              <TableCell>{t('settings.rampsSettings.rampEditor.gisShapeEditGrid.headers.fontSize')}</TableCell>
              <TableCell>{t('settings.rampsSettings.rampEditor.gisShapeEditGrid.headers.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shapes.map((shape) => (
              <ShapeEditRow key={shape.id} shape={shape} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {shapes.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {t('settings.rampsSettings.rampEditor.gisShapeEditGrid.shapeCount', { count: shapes.length })}
        </Typography>
      )}
    </Box>
  );
};
